<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	import { real_id } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		DoubleDeviceButton,
		DoubleDeviceButtonV2,
		Text,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import PinUnlock from '../../PinUnlock.svelte';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { wait } from 'src/helpers/utilities';
	import { hasControlRights } from 'src/helpers/user_helpers';

	let className = '';
	export { className as class };
	export let device;
	export let useV2Components = false;
	$: deviceId = real_id(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};

	let thermostat = $settingsStore?.['thermostat:0'];
	$: unit = thermostat?.display_unit || 'C';
	const min = 5;
	const max = 35;
	$: settings = $settingsStore;
	let statusStore = getDeviceStatus(device.id);
	let status = $statusStore;

	let value = thermostat?.target_C || 'N/A',
		cacheValue = value;
	let disabled = value === 'N/A' || typeof value === 'undefined';
	let reqLocked = false;
	let isLoading = false;
	let pinLocked;
	let showModal = false;
	let lockValue = false;
	let callbackValue;
	let sendTimeout;

	$: hasRights = hasControlRights(device);

	beforeUpdate(() => {
		const thermostatSettings = $settingsStore?.['thermostat:0'];

		if (thermostatSettings === undefined) return;

		thermostat = thermostatSettings;
		status = $statusStore;
		settings = $settingsStore;

		const statusThermostat = status?.['thermostat:0'];
		const deviceData = $devices[deviceId];

		// Update disabled state
		disabled = !thermostat?.enable || typeof thermostat?.target_C === 'undefined' || value === 'N/A';

		// Update pin lock state
		pinLocked = typeof $pin_unlocked_devices[deviceId] === 'undefined' && deviceData?.pin_lock;

		// Update value if needed (only when not locked and not loading)
		if (!lockValue && !isLoading && (value === cacheValue || typeof value === 'undefined')) {
			const newValue =
				thermostat?.target_C !== statusThermostat?.target_C
					? statusThermostat?.target_C
					: thermostat?.target_C;
			value = newValue;
			cacheValue = newValue;
		}

		reqLocked = $req_locked_devices.indexOf(deviceId) !== -1;
	});

	/**
	 * Function to set temperature, it will send request to device and lock input during request
	 * @param {number} _value - new temperature value
	 * @returns {Promise<void>}
	 */
	const temperatureSet = async (_value) => {
		lockValue = true;
		isLoading = true;

		const data = { ...thermostat, target_C: _value };
		const channel = device?.shared ? device?.channel : 0;

		const res = await gen2Request(device, 'Thermostat.SetConfig', {
			id: 0,
			config: { ...data, channel },
		});

		const response = onSettingsResponse(res, device, {
			success: $format('temp_set_scc_toast', { values: { value: _value } }),
		});

		if (response.isok) {
			// Update immediately to prevent bouncing
			thermostat = { ...thermostat, ...data };
			devices_settings.update_devices_settings(real_id(device.id), settings);
			cacheValue = _value;

			// Keep lock for a short time to prevent immediate re-updates
			wait(500).then(() => {
				lockValue = false;
			});
		} else {
			lockValue = false;
		}

		isLoading = false;
		return response;
	};

	const onChange = async (e: CustomEvent) => {
		//debouncing on change for new buttons control
		lockValue = true;
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(temperatureSet, 1000, e.detail.value);
	};

	let interval;

	const handleTemperatureChange = (val) => {
		if (pinLocked) {
			callbackValue = val;
			showModal = true;
			return;
		}

		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}

		const step = 0.5;
		const newValue = val === 0 ? value - step : value + step;
		value = Math.max(min, Math.min(max, Number(newValue.toFixed(1))));

		isLoading = true;
		onChange({ detail: { value } } as CustomEvent);
	};

	const handleLongPress = (val) => {
		if (!pinLocked) {
			interval = setInterval(() => {
				handleTemperatureChange(val);
			}, 100);
		}
	};

	const handlePin = () => {
		// using real id because thermostats id is _101 but we need not to change pin lock logic so if device has pin lock it's channel will have it also
		const _device = { ...device, id: real_id(device?.id) };
		pinLocked = unlockPin(_device, pinLocked);
		handleTemperatureChange(callbackValue);
	};
</script>

{#if useV2Components}
	<DoubleDeviceButtonV2
		id="doubledevicebutton-836ao0v"
		{isLoading}
		disabled={disabled || !hasRights}
		class={className}
		aria-label={$format('button_group_temperature_change', {
			default: 'Handle temperature change ',
		})}
		{...devBtnsV2Props.doubleDeviceButtonV2}
	>
		<DeviceButtonV2
			id="devicebutton-k6wqp56"
			aria-label={$format('button_decrease_temperature', {
				default: 'Decrease temperature',
			})}
			slot="buttonLeft"
			icon="fas fa-minus"
			{disabled}
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(0);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(0);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
		<svelte:fragment slot="content">
			<Text styleType="body-extra-small" class="text-bold mb-xxs width-full" noWrap>
				{value}
			</Text>
			<Text styleType="tiny">{`°${unit}`}</Text>
		</svelte:fragment>
		<DeviceButtonV2
			id="devicebutton-xbhvt53"
			aria-label={$format('button_increase_temperature', {
				default: 'Increase temperature',
			})}
			slot="buttonRight"
			icon="fas fa-plus"
			variant="danger"
			{disabled}
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(1);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(1);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
	</DoubleDeviceButtonV2>
{:else}
	<DoubleDeviceButton
		id="doubledevicebutton-836ao0v"
		{isLoading}
		disabled={disabled || !hasRights}
		class={className}
		aria-label={$format('button_group_temperature_change', {
			default: 'Handle temperature change ',
		})}
	>
		<DeviceButton
			id="devicebutton-k6wqp56"
			aria-label={$format('button_decrease_temperature', {
				default: 'Decrease temperature',
			})}
			slot="buttonLeft"
			icon="fas fa-minus"
			{disabled}
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(0);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(0);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
		/>
		<svelte:fragment slot="content">
			<Text styleType="controls-large" class="text-bold">
				{value}
			</Text>
			<Text styleType="controls-small">{`°${unit}`}</Text>
		</svelte:fragment>
		<DeviceButton
			id="devicebutton-xbhvt53"
			aria-label={$format('button_increase_temperature', {
				default: 'Increase temperature',
			})}
			slot="buttonRight"
			icon="fas fa-plus"
			variant="danger"
			{disabled}
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(1);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(1);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
		/>
	</DoubleDeviceButton>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
